import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  FileText,
  Plus,
  Search,
  Calendar,
  Clock,
  AlertTriangle,
  User,
  Filter,
  Trash2,
  MoreVertical,
  Upload,
  Paperclip,
  Image,
  X,
  Download,
  Eye,
  FileDown,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import ResidentDocumentPrint from './ResidentDocumentPrint';
import { escapeDeep, escapeHtml } from '../lib/escapeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseNotes = () => {
  const { user } = useAuth();
  const [caseNotes, setCaseNotes] = useState([]);
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printResident, setPrintResident] = useState(null);
  
  const [formData, setFormData] = useState({
    resident_id: '',
    note: '',
    is_urgent: false
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchResidents();
  }, []);

  useEffect(() => {
    if (selectedResident) {
      fetchCaseNotes(selectedResident);
    } else {
      setCaseNotes([]);
      setLoading(false);
    }
  }, [selectedResident]);

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching residents:', error);
      toast.error('Failed to load residents');
      setLoading(false);
    }
  };

  const fetchCaseNotes = async (residentId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/case-notes/resident/${residentId}`);
      setCaseNotes(response.data);
    } catch (error) {
      console.error('Error fetching case notes:', error);
      toast.error('Failed to load case notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.resident_id || !formData.note.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData for file upload
      const uploadData = new FormData();
      uploadData.append('resident_id', formData.resident_id || selectedResident);
      uploadData.append('note', formData.note);
      uploadData.append('is_urgent', formData.is_urgent);
      
      // Add files to FormData
      selectedFiles.forEach((file, index) => {
        uploadData.append('files', file);
      });

      await axios.post(`${API}/case-notes`, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(`Case note added successfully${selectedFiles.length > 0 ? ` with ${selectedFiles.length} attachment(s)` : ''}`);
      resetForm();
      
      // Refresh case notes for the selected resident
      if (selectedResident) {
        fetchCaseNotes(selectedResident);
      }
    } catch (error) {
      console.error('Error adding case note:', error);
      toast.error(error.response?.data?.detail || 'Failed to add case note');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('application/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        toast.error(`${file.name}: Only images and PDF files are allowed`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name}: File size must be less than 10MB`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      resident_id: '',
      note: '',
      is_urgent: false
    });
    setSelectedFiles([]);
    setShowAddDialog(false);
  };

  const handleViewFile = async (fileName) => {
    try {
      // Check if user is authenticated
      if (!user) {
        toast.error('Authentication required to view files');
        return;
      }

      const response = await axios.get(`${API}/case-notes/file/${fileName}`, {
        responseType: 'blob'
      });
      
      // Get the content type from response headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create blob with proper content type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      // For images, open in new tab. For other files, try to open or download
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
      
      if (imageExtensions.includes(fileExtension) || contentType.startsWith('image/')) {
        // Open images in new tab
        window.open(url, '_blank');
      } else if (fileExtension === 'pdf' || contentType === 'application/pdf') {
        // Open PDFs in new tab
        window.open(url, '_blank');
      } else {
        // For other file types, try to open or download
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
      }
      
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      
      toast.success('File opened successfully');
    } catch (error) {
      console.error('Error viewing file:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication required to view this file');
      } else {
        toast.error('Failed to open file');
      }
    }
  };

  const handleDownloadFile = async (fileName) => {
    try {
      // Check if user is authenticated
      if (!user) {
        toast.error('Authentication required to download files');
        return;
      }

      const response = await axios.get(`${API}/case-notes/file/${fileName}`, {
        responseType: 'blob'
      });
      
      // Get the content type from response headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create blob with proper content type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication required to download this file');
      } else {
        toast.error('Failed to download file');
      }
    }
  };

  const exportCaseNotesToPDF = async (residentId = null) => {
    try {
      const notesToExport = residentId ? 
        caseNotes.filter(note => note.resident_id === residentId) : 
        filteredNotes;
      
      if (notesToExport.length === 0) {
        toast.error('No case notes to export');
        return;
      }

      const resident = residents.find(r => r.id === (residentId || selectedResident));
      const residentName = escapeHtml(resident ? `${resident.first_name} ${resident.last_name}` : 'All Residents');
      
      // Generate PDF using browser's print functionality
      const printWindow = window.open('', '_blank');
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Case Notes - ${residentName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 40px; 
              line-height: 1.6; 
              color: #333;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
              margin-bottom: 30px; 
            }
            .facility-name { 
              font-size: 24px; 
              font-weight: bold; 
              color: #2563eb; 
              margin-bottom: 5px; 
            }
            .document-title { 
              font-size: 20px; 
              margin-bottom: 10px; 
            }
            .resident-info { 
              font-size: 16px; 
              color: #666; 
            }
            .case-note { 
              margin-bottom: 30px; 
              border: 1px solid #ddd; 
              padding: 20px; 
              break-inside: avoid; 
            }
            .note-header { 
              display: flex; 
              justify-content: space-between; 
              border-bottom: 1px solid #eee; 
              padding-bottom: 10px; 
              margin-bottom: 15px; 
            }
            .note-meta { 
              font-size: 12px; 
              color: #666; 
            }
            .urgent { 
              background-color: #fef2f2; 
              border-color: #fca5a5; 
            }
            .urgent .note-header { 
              color: #dc2626; 
            }
            .note-content { 
              font-size: 14px; 
              margin-bottom: 15px; 
            }
            .attachments { 
              font-size: 12px; 
              color: #666; 
              font-style: italic; 
            }
            .footer { 
              margin-top: 40px; 
              text-align: center; 
              font-size: 12px; 
              color: #666; 
              border-top: 1px solid #ddd; 
              padding-top: 20px; 
            }
            @page { 
              margin: 1in; 
            }
            @media print { 
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="facility-name">Shelter Care Connect</div>
            <div class="document-title">Case Notes Report</div>
            <div class="resident-info">
              Resident: ${residentName}<br>
              Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
              Total Notes: ${notesToExport.length}
            </div>
          </div>
          
          ${escapeDeep(notesToExport).map(note => {
            const date = new Date(note.timestamp);
            return `
              <div class="case-note ${note.is_urgent ? 'urgent' : ''}">
                <div class="note-header">
                  <div>
                    <strong>Staff:</strong> ${note.staff_name}${note.is_urgent ? ' <span style="color: #dc2626; font-weight: bold;">[URGENT]</span>' : ''}
                  </div>
                  <div class="note-meta">
                    ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}
                  </div>
                </div>
                <div class="note-content">
                  ${note.note.replace(/\n/g, '<br>')}
                </div>
                ${note.attachments && note.attachments.length > 0 ? `
                  <div class="attachments">
                    Attachments: ${note.attachments.length} file(s) - ${note.attachments.map(att => att.split('/').pop()).join(', ')}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
          
          <div class="footer">
            <p>This document contains confidential resident information. Handle according to privacy policies.</p>
            <p>Generated by Shelter Care Connect - Case Management System</p>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success('Case notes document opened for printing/saving');
      
    } catch (error) {
      console.error('Error exporting case notes:', error);
      toast.error('Failed to export case notes');
    }
  };

  const exportSingleNoteToWord = (note) => {
    try {
      const resident = residents.find(r => r.id === note.resident_id);
      const date = new Date(note.timestamp);
      
      const wordContent = `
SHELTER CARE CONNECT
CASE NOTE RECORD

================================

RESIDENT: ${resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown'}
STAFF MEMBER: ${note.staff_name}
DATE & TIME: ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}
${note.is_urgent ? 'PRIORITY: *** URGENT ***' : 'PRIORITY: Normal'}

================================

CASE NOTE:

${note.note}

${note.attachments && note.attachments.length > 0 ? `
ATTACHMENTS:
${note.attachments.map(att => `- ${att.split('/').pop()}`).join('\n')}
` : ''}

================================

This document contains confidential resident information.
Generated by Shelter Care Connect Case Management System
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
      `;
      
      // Create blob and download
      const blob = new Blob([wordContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CaseNote_${resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown'}_${date.toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Case note downloaded as document file');
      
    } catch (error) {
      console.error('Error exporting single note:', error);
      toast.error('Failed to export case note');
    }
  };

  const handlePrintCaseNotes = (resident, notesToPrint) => {
    const residentData = {
      ...resident,
      printCaseNotes: notesToPrint || []
    };
    setPrintResident(residentData);
    setShowPrintDialog(true);
  };

  const handleDeleteCaseNote = async (note) => {
    if (!user || user.role !== 'admin') {
      toast.error('Only administrators can delete case notes');
      return;
    }

    // Open confirmation dialog instead of window.confirm
    setNoteToDelete(note);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteCaseNote = async () => {
    if (!noteToDelete) return;

    try {
      await axios.delete(`${API}/case-notes/${noteToDelete.id}`);
      toast.success('Case note deleted successfully');
      setShowDeleteConfirmDialog(false);
      setNoteToDelete(null);
      // Refresh case notes
      if (selectedResident) {
        fetchCaseNotes(selectedResident);
      }
    } catch (error) {
      console.error('Error deleting case note:', error);
      toast.error('Failed to delete case note');
      setShowDeleteConfirmDialog(false);
      setNoteToDelete(null);
    }
  };

  const cancelDeleteCaseNote = () => {
    setShowDeleteConfirmDialog(false);
    setNoteToDelete(null);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const filteredNotes = caseNotes.filter(note =>
    note.note.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.staff_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedResidentInfo = residents.find(r => r.id === selectedResident);

  return (
    <div className="space-y-6 fade-in">
      {/* Header with Add Case Note Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FileText className="h-8 w-8 text-indigo-600" />
            <span>Case Notes</span>
          </h1>
          <p className="text-gray-600 mt-1">Document and track resident interactions</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {/* Export buttons - show when there are notes to export */}
          {filteredNotes.length > 0 && (
            <>
              <Button 
                onClick={() => exportCaseNotesToPDF(selectedResident)}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 flex items-center space-x-2"
              >
                <FileDown className="h-4 w-4" />
                <span>Export PDF</span>
              </Button>
              
              <Button 
                onClick={() => {
                  if (!selectedResident) {
                    toast.error('Please select a resident first');
                    return;
                  }
                  
                  const resident = residents.find(r => r.id === selectedResident);
                  const residentCaseNotes = caseNotes.filter(note => note.resident_id === selectedResident);
                  
                  if (residentCaseNotes.length === 0) {
                    toast.error('No case notes found for this resident');
                    return;
                  }
                  
                  console.log('DEBUG Print Data:', {
                    selectedResidentId: selectedResident,
                    foundResident: resident,
                    residentName: resident ? `${resident.first_name} ${resident.last_name}` : 'Not found',
                    noteCount: residentCaseNotes.length,
                    allResidents: residents.map(r => ({ id: r.id, name: `${r.first_name} ${r.last_name}` })),
                    notes: residentCaseNotes
                  });
                  
                  if (!resident) {
                    toast.error('Resident not found. Please select a resident.');
                    return;
                  }
                  
                  handlePrintCaseNotes(resident, residentCaseNotes);
                }}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50 flex items-center space-x-2"
              >
                <Printer className="h-4 w-4" />
                <span>Print Report</span>
              </Button>
              
              <Button 
                onClick={() => {
                  if (selectedResident) {
                    const residentNotes = caseNotes.filter(note => note.resident_id === selectedResident);
                    if (residentNotes.length > 0) {
                      // Export all notes for this resident as combined document
                      const resident = residents.find(r => r.id === selectedResident);
                      
                      let combinedContent = 'SHELTER CARE CONNECT\nCOMPLETE CASE NOTES RECORD\n\n================================\n\n';
                      combinedContent += `RESIDENT: ${resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown'}\n`;
                      combinedContent += `TOTAL NOTES: ${residentNotes.length}\n`;
                      combinedContent += `EXPORT DATE: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}\n\n`;
                      combinedContent += '================================\n\n';
                      
                      residentNotes.forEach((note, index) => {
                        const date = new Date(note.timestamp);
                        combinedContent += `NOTE #${index + 1}\n`;
                        combinedContent += `Staff: ${note.staff_name}\n`;
                        combinedContent += `Date: ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}\n`;
                        combinedContent += `${note.is_urgent ? 'PRIORITY: *** URGENT ***' : 'Priority: Normal'}\n\n`;
                        combinedContent += `${note.note}\n\n`;
                        
                        if (note.attachments && note.attachments.length > 0) {
                          combinedContent += `Attachments: ${note.attachments.map(att => att.split('/').pop()).join(', ')}\n`;
                        }
                        
                        combinedContent += '\n================================\n';
                      });
                      
                      combinedContent += '\nThis document contains confidential resident information.\n';
                      combinedContent += 'Generated by Shelter Care Connect Case Management System';
                      
                      const blob = new Blob([combinedContent], { type: 'text/plain;charset=utf-8' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `CaseNotes_${resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown'}_Complete_${new Date().toISOString().split('T')[0]}.txt`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      toast.success('Complete case notes downloaded as document');
                    }
                  }
                }}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50 flex items-center space-x-2"
              >
                <FileDown className="h-4 w-4" />
                <span>Export All</span>
              </Button>
            </>
          )}
          
          <Button 
            onClick={() => setShowAddDialog(true)} 
            className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Case Note</span>
          </Button>
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Case Note</DialogTitle>
              <DialogDescription>
                Document an interaction or observation about a resident
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Select Resident *</label>
                <Select
                  value={formData.resident_id}
                  onValueChange={(value) => setFormData({...formData, resident_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a resident..." />
                  </SelectTrigger>
                  <SelectContent>
                    {residents.map((resident) => (
                      <SelectItem key={resident.id} value={resident.id}>
                        {resident.first_name} {resident.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Case Note *</label>
                <Textarea
                  required
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Enter detailed case note about the resident interaction, observation, or incident..."
                  rows={6}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="urgent"
                  checked={formData.is_urgent}
                  onCheckedChange={(checked) => setFormData({...formData, is_urgent: checked})}
                />
                <label htmlFor="urgent" className="text-sm font-medium text-gray-700">
                  Mark as urgent
                </label>
              </div>

              {/* File Upload Section */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                  <Paperclip className="h-4 w-4" />
                  <span>Attach Documents/Photos</span>
                </label>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-2">
                      <Upload className="h-8 w-8 text-gray-400" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Click to upload files</span>
                        <p className="text-xs text-gray-500">Images, PDFs, or documents up to 10MB each</p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Selected Files:</p>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            {file.type.startsWith('image/') ? (
                              <Image className="h-4 w-4 text-blue-600" />
                            ) : (
                              <FileText className="h-4 w-4 text-red-600" />
                            )}
                            <span className="text-sm text-gray-700 truncate max-w-xs">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024 / 1024).toFixed(1)} MB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            onClick={() => removeFile(index)}
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={uploading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Upload className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Case Note
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* Resident Selection and Search */}
      <Card className="card-shadow">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Resident</label>
              <Select value={selectedResident} onValueChange={setSelectedResident}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a resident to view their case notes..." />
                </SelectTrigger>
                <SelectContent>
                  {residents.map((resident) => (
                    <SelectItem key={resident.id} value={resident.id}>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{resident.first_name} {resident.last_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedResident && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
            )}
          </div>
          
          {selectedResidentInfo && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">
                  {selectedResidentInfo.first_name} {selectedResidentInfo.last_name}
                </h3>
              </div>
              <div className="mt-2 text-sm text-blue-700">
                <p>Intake Date: {new Date(selectedResidentInfo.intake_date).toLocaleDateString()}</p>
                <p>Stay Length: {selectedResidentInfo.stay_length_days} days</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Case Notes Display */}
      {!selectedResident ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Resident</h3>
            <p className="text-gray-600 mb-4">Choose a resident from the dropdown above to view their case notes.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-24 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredNotes.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No matching case notes' : 'No case notes yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : `There are no case notes for ${selectedResidentInfo?.first_name} ${selectedResidentInfo?.last_name} yet.`
              }
            </p>
            {!searchTerm && (
              <Button 
                onClick={() => {
                  setFormData({...formData, resident_id: selectedResident});
                  setShowAddDialog(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Case Note
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredNotes.map((note) => {
            const { date, time } = formatDateTime(note.timestamp);
            
            return (
              <Card key={note.id} className={`card-shadow hover:card-shadow-lg transition-shadow ${
                note.is_urgent ? 'border-l-4 border-l-red-500' : ''
              }`}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full ${
                        note.is_urgent ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                        {note.is_urgent ? (
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                        ) : (
                          <FileText className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <CardTitle className="text-lg">Case Note</CardTitle>
                          {note.is_urgent && (
                            <Badge className="bg-red-100 text-red-800 border-red-200">
                              Urgent
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="flex items-center space-x-4 mt-1">
                          <span className="flex items-center space-x-1">
                            <User className="h-3 w-3" />
                            <span>{note.staff_name}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{date}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{time}</span>
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                    
                    {/* Export and Admin Buttons */}
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleNoteToWord(note)}
                        className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                        title="Export this note as document"
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const resident = residents.find(r => r.id === selectedResident);
                          handlePrintCaseNotes(resident, [note]);
                        }}
                        className="text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                        title="Print this note"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      
                      {user?.role === 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCaseNote(note)}
                          className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          title="Delete this note"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className={`p-4 rounded-lg ${
                    note.is_urgent ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <p className={`text-sm leading-relaxed ${
                      note.is_urgent ? 'text-red-900' : 'text-gray-700'
                    }`}>
                      {note.note}
                    </p>
                  </div>

                  {/* File Attachments */}
                  {note.attachments && note.attachments.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                        <Paperclip className="h-4 w-4" />
                        <span>Attachments ({note.attachments.length})</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {note.attachments.map((attachment, index) => {
                          // Extract filename from full path (handle both Unix and Windows paths)
                          const fileName = attachment.split(/[/\\]/).pop();
                          const fileExtension = fileName?.split('.').pop()?.toLowerCase();
                          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension);
                          
                          return (
                            <div key={index} className="flex items-center space-x-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                              {isImage ? (
                                <Image className="h-4 w-4 text-blue-600" />
                              ) : (
                                <FileText className="h-4 w-4 text-blue-600" />
                              )}
                              <span className="text-sm text-blue-900 truncate flex-1" title={fileName}>
                                {fileName}
                              </span>
                              <div className="flex space-x-1">
                                <Button
                                  onClick={() => handleViewFile(fileName)}
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-blue-600 hover:bg-blue-100"
                                  title="View/Download"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button
                                  onClick={() => handleDownloadFile(fileName)}
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-blue-600 hover:bg-blue-100"
                                  title="Download"
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Case Note Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Case Note</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this case note? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {noteToDelete && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <p className="font-medium text-red-800">
                  📝 Case Note from {noteToDelete.created_at ? new Date(noteToDelete.created_at).toLocaleDateString() : 'Unknown date'}
                </p>
                <p className="text-sm text-red-700">
                  👤 Staff: {noteToDelete.staff_name || 'Unknown staff'}
                </p>
                {noteToDelete.is_urgent && (
                  <p className="text-sm font-medium text-red-800">
                    🚨 URGENT NOTE
                  </p>
                )}
                <p className="text-sm text-red-600 bg-white p-2 rounded border">
                  {noteToDelete.note ? 
                    (noteToDelete.note.length > 100 ? 
                      noteToDelete.note.substring(0, 100) + "..." : 
                      noteToDelete.note
                    ) : 
                    'No content preview available'
                  }
                </p>
                {noteToDelete.attached_files && noteToDelete.attached_files.length > 0 && (
                  <p className="text-sm text-red-600">
                    📎 {noteToDelete.attached_files.length} attached file(s) will also be deleted
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={cancelDeleteCaseNote}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteCaseNote}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Case Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      {printResident && (
        <ResidentDocumentPrint
          isOpen={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          resident={printResident}
          caseNotes={printResident.printCaseNotes || []}
          printType="case_notes"
        />
      )}
    </div>
  );
};

export default CaseNotes;